import { type RateConfiguration } from "@src/appV2/ExperimentalHomeHealth/lib/rateConfigurationUtils";

import { PricingType } from "../types";
import {
  FIRST_VISIT_DUE_DATE_IN_PAST_ERROR,
  isFirstVisitDueDateInPast,
} from "./firstVisitDueDateValidation";

export interface MobileCaseStep2NumericFields {
  visitsPerWeek: string;
  totalWeeks: string;
  chargePerVisit: string;
  payPerVisit: string;
}

export type MobileCaseStep2FieldErrors = Partial<
  Record<keyof MobileCaseStep2NumericFields | "firstVisitDueDate", string>
>;

// Counts (visits per week, total weeks) must be a whole number of at least 1.
// An empty value is treated as "not filled in yet" rather than invalid — the
// wizard's presence check keeps the Next button disabled in that case — so the
// poster isn't nagged with an error before they've typed anything.
function positiveWholeNumberError(rawValue: string, fieldLabel: string): string | undefined {
  if (rawValue.trim() === "") {
    return undefined;
  }

  const value = Number(rawValue);
  if (!Number.isInteger(value) || value < 1) {
    return `${fieldLabel} must be a whole number greater than 0`;
  }

  return undefined;
}

// Rates must fall within the workplace's configured range. Naming the field and
// spelling out the bounds gives the poster the context the previous server-only
// "Something went wrong" error lacked.
function rateRangeError(
  rawValue: string,
  fieldLabel: string,
  minimum: number,
  maximum: number
): string | undefined {
  if (rawValue.trim() === "") {
    return undefined;
  }

  const value = Number(rawValue);
  if (Number.isNaN(value) || value < minimum || value > maximum) {
    return `${fieldLabel} must be between $${minimum.toFixed(2)} and $${maximum.toFixed(2)}`;
  }

  return undefined;
}

/**
 * Client-side validation for the mobile case-posting rate/schedule step,
 * mirroring the desktop VisitRatesForm rules. Only returns a message for a field
 * that holds a non-empty, invalid value; empty (unfilled) fields are omitted.
 * `todayIso` is the workplace's "today" (`yyyy-MM-dd`) used to reject a
 * first-visit due date in the past (GRO-2550).
 */
export function getMobileCaseStep2FieldErrors(params: {
  data: MobileCaseStep2NumericFields & { firstVisitDueDate: string };
  rates: RateConfiguration;
  pricingType: PricingType;
  todayIso: string;
}): MobileCaseStep2FieldErrors {
  const { data, rates, pricingType, todayIso } = params;
  const rateLabel = pricingType === PricingType.PER_HOUR ? "per hour" : "per visit";
  const errors: MobileCaseStep2FieldErrors = {};

  // The native date input's `min` attribute doesn't reliably block a typed or
  // pasted past date on every platform, so reject it here too.
  if (isFirstVisitDueDateInPast(data.firstVisitDueDate, todayIso)) {
    errors.firstVisitDueDate = FIRST_VISIT_DUE_DATE_IN_PAST_ERROR;
  }

  const visitsPerWeekError = positiveWholeNumberError(data.visitsPerWeek, "Visits per week");
  if (visitsPerWeekError) {
    errors.visitsPerWeek = visitsPerWeekError;
  }

  const totalWeeksError = positiveWholeNumberError(data.totalWeeks, "Total weeks");
  if (totalWeeksError) {
    errors.totalWeeks = totalWeeksError;
  }

  // Rate fields are only shown (and only submitted) when the poster can set
  // rates, so skip validating them otherwise.
  if (rates.canSetRates) {
    const chargeError = rateRangeError(
      data.chargePerVisit,
      `Charge ${rateLabel}`,
      rates.chargeRate.minimum,
      rates.chargeRate.maximum
    );
    if (chargeError) {
      errors.chargePerVisit = chargeError;
    }

    const payError = rateRangeError(
      data.payPerVisit,
      `Pay ${rateLabel}`,
      rates.payRate.minimum,
      rates.payRate.maximum
    );
    if (payError) {
      errors.payPerVisit = payError;
    }
  }

  return errors;
}
